import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { getSettings, updateSetting } from '../services/api';

interface AdminSetting {
  name: string;
  type: 'text' | 'options' | 'date';
  options: string[];
  value: any;
  description: string;
}

const Settings: React.FC = () => {
  const [settings, setSettings] = useState<AdminSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedSetting, setSelectedSetting] = useState<AdminSetting | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [editOptions, setEditOptions] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await getSettings();
      if (response.status === 'success') {
        setSettings(response.data);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleEditClick = (setting: AdminSetting) => {
    setSelectedSetting(setting);
    setEditValue(setting.value || '');
    setEditOptions(setting.options.join(', '));
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!selectedSetting) return;

    try {
      const data: any = { value: editValue };
      if (selectedSetting.type === 'options') {
        data.options = editOptions.split(',').map((o) => o.trim()).filter((o) => o !== '');
      }

      const response = await updateSetting(selectedSetting.name, data);
      if (response.status === 'success') {
        setSuccessMessage(`Setting "${selectedSetting.name}" updated successfully`);
        setEditDialogOpen(false);
        fetchSettings();
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update setting');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Admin Settings
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Setting Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Value / Options</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {settings.map((setting) => (
              <TableRow key={setting.name}>
                <TableCell sx={{ fontWeight: 'bold' }}>{setting.name}</TableCell>
                <TableCell>{setting.description}</TableCell>
                <TableCell>
                  <Chip label={setting.type} size="small" color="primary" variant="outlined" />
                </TableCell>
                <TableCell>
                  {setting.type === 'options' ? (
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {setting.options.map((opt, i) => (
                        <Chip key={i} label={opt} size="small" />
                      ))}
                    </Box>
                  ) : (
                    setting.value
                  )}
                </TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleEditClick(setting)} color="primary">
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Setting: {selectedSetting?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="textSecondary">
              {selectedSetting?.description}
            </Typography>
            
            <TextField
              label="Value"
              fullWidth
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              type={selectedSetting?.type === 'date' ? 'date' : 'text'}
              InputLabelProps={selectedSetting?.type === 'date' ? { shrink: true } : {}}
            />

            {selectedSetting?.type === 'options' && (
              <TextField
                label="Options (comma separated)"
                fullWidth
                multiline
                rows={3}
                value={editOptions}
                onChange={(e) => setEditOptions(e.target.value)}
                helperText="Enter options separated by commas (e.g. Lahore, Islamabad, Multan)"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings;
